import "./modelstyle.scss";

import {
  BoltOutlined,
  CloudDownloadOutlined,
  MapOutlined,
  ModelTraining,
  PlayArrow,
  Stop,
} from "@mui/icons-material";
import {
  CAlert,
  CButton,
  CCol,
  CFormGroup,
  CInput,
  CInputCheckbox,
  CInputRadio,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
  CModalTitle,
} from "@coreui/react";
import {
  ModelService,
  useDeleteModel,
  useDeployModel,
  useGetModel,
  useGetModelVersion,
  useMatchingPersonas,
  useSwapPersonasToModel,
  useUnDeployModel,
} from "./ModelService";
import ModelComboSelect from "src/reusable/components/ModelComboSelect";
import { NucToast } from "src/reusable/components/Toast";
import React, { useEffect, useRef, useState, useCallback } from "react";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import Button from "@mui/material/Button";
import CIcon from "@coreui/icons-react";
import { ConfirmModal as ConfirmActionModal } from "./LegacyModelModals";
import { CreateItemButton } from "../forms/FormUtils";
import { CreateModelVersionModal } from "../forms/ModelVersionModals";
import { LoadingButton } from "@mui/lab";
import { ModelInferForm } from "./Models";
import { AtomAssistProductID, ProjectAIProductID } from "src/reusable/products";
import PropTypes from "prop-types";
import {
  SyncRemoteModelModal,
  UpdateRemoteUrlModal,
} from "../forms/ModelModals";
import { freeSet } from "@coreui/icons";
import { useHistory } from "react-router-dom";
import ErrorMessageAlerts from "src/reusable/ErrorMessageAlerts";
import { Tooltip } from "@mui/material";
import { Loading } from "src/reusable/LoadingSpinner";
import { debounce } from "src/reusable/Functions";
import { ModelRelatedPersonas } from "./ModelRelatedPersonas";

export class ModelTrainButton extends React.Component {
  constructor(props) {
    super(props);
    this.modal = React.createRef();
    this.model_version_number = props.version_number;
    this.confirmMessage = `You are about to train the ${props.model.name}, Version ${this.model_version_number} model.`;

    this.confirmAction = this.confirmAction.bind(this);
  }

  confirmAction() {
    ModelService.train_model(
      this.props.model.id,
      this.model_version_number,
      (data) => {
        this.props.onActionComplete(data);
      },
    );
  }

  render() {
    return (
      <>
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.modal.current.showModal()}
          disabled={this.props.status_fk === "retired"}
          sx={{ ...this.props.sx }}
          startIcon={<ModelTraining />}
          data-attr="models:btn:train-model"
        >
          Train Model
        </Button>
        <ConfirmActionModal
          ref={this.modal}
          confirmMessage={this.confirmMessage}
          titleMessage={this.props.model.name}
          confirmAction={this.confirmAction}
        />
      </>
    );
  }
}

ModelTrainButton.propTypes = {
  model: PropTypes.object.isRequired,
  version_number: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  status_fk: PropTypes.string.isRequired,
  onActionComplete: PropTypes.func.isRequired,
  sx: PropTypes.any,
};

export const ModelDetailsButton = (props) => {
  const history = useHistory();
  // Hard coded to 5 since the project Genius product ID is 5 and this is the only one we currently exclude
  return (
    <>
      <Button
        block={props.block}
        variant="contained"
        color="primary"
        className="button-hover-darken"
        id={`model-details-button-${props.model.name}`}
        data-testid={`mdl-btn-see-details-${props.model.id}`}
        data-attr={`models:btn:view-model-details-${props.model.id}`}
        style={{
          height: "35px",
          whiteSpace: "nowrap",
          ...props.style,
        }}
        onClick={(e) => {
          e.preventDefault();
          history.push(`/modeldetails/${props.model.id}`);
        }}
      >
        See Details
      </Button>
    </>
  );
};

ModelDetailsButton.propTypes = {
  model: PropTypes.object.isRequired,
  block: PropTypes.bool,
  variant: PropTypes.oneOf(["outline", "ghost", null]),
  style: PropTypes.any,
};

ModelDetailsButton.defaultProps = {
  block: true,
  variant: "outline",
};

export const ModelDeployToggleButton = (props) => {
  const { data: modelVersion, isLoading: isLoadingModelVersion } =
    useGetModelVersion(props.model_id, props.model_version);
  const { data: model } = useGetModel(props.model_id);
  const {
    mutate: deployModel,
    isLoading: isDeploying,
    isSuccess: isDeployed,
    isError: isDeployError,
    reset: resetDeploy,
    error: deployError,
  } = useDeployModel(props.model_id, props.model_version);

  const {
    mutate: unDeployModel,
    isLoading: isUnDeploying,
    isSuccess: isUnDeployed,
    isError: isUnDeployError,
    reset: resetUnDeploy,
    error: unDeployError,
  } = useUnDeployModel(props.model_id, props.model_version);

  if (isDeployed) {
    resetDeploy();
  }
  if (isUnDeployed) {
    resetUnDeploy();
  }
  const isLoading = isLoadingModelVersion || isDeploying || isUnDeploying;
  if (isDeployError || isUnDeployError) {
    return (
      <CAlert color="danger" style={{ fontSize: "75%" }}>
        {isDeployError && String(deployError?.response?.data?.detail)}
        {isUnDeployError && String(unDeployError?.response?.data?.detail)}
      </CAlert>
    );
  }
  const disabled = !model || model?.remote_model;

  if (!modelVersion?.is_deployed) {
    return (
      <LoadingButton
        color="success"
        className="model-deploy-button"
        variant="contained"
        startIcon={!disabled && <PlayArrow />}
        loading={isLoading}
        loadingPosition="start"
        onClick={deployModel}
        disabled={
          disabled ||
          props.model?.model_products[0]?.products?.id === ProjectAIProductID
        }
        sx={{ ...props.sx }}
        data-testid={`mdl-version-deploy-${props.model_version}`}
        data-attr={`models:btn:deploy-version-${props.model_version}`}
      >
        <span>{!disabled ? "Deploy" : "Undeployed"}</span>
      </LoadingButton>
    );
  }
  return (
    <LoadingButton
      color="error"
      className="model-deploy-button"
      variant="contained"
      startIcon={!disabled && <Stop />}
      loading={isLoading}
      loadingPosition="start"
      onClick={unDeployModel}
      disabled={disabled}
      sx={{ ...props.sx }}
      data-testid={`mdl-version-undeploy-${props.model_version}`}
      data-attr={`models:btn:undeploy-version-${props.model_version}`}
    >
      <span>{!disabled ? "Undeploy" : "Deployed"}</span>
    </LoadingButton>
  );
};

ModelDeployToggleButton.propTypes = {
  model_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  model_version: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  model: PropTypes.object,
  sx: PropTypes.any,
};

export function getLatestDeployedModel(mvlist) {
  let ldm = mvlist.filter((m) => m.is_deployed);
  ldm.sort((m) => m.version_number);
  return ldm[0];
}

export function ModelInferModalButton(props) {
  const [show, toggleShow] = useState(false);

  return (
    <>
      {show && (
        <CModal
          show={show}
          onClose={() => {
            toggleShow(!show);
          }}
          className="w-100 text-center"
        >
          <CModalHeader closeButton>
            <CModalTitle>{props.model.name}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {props.modelversion.is_deployed && (
              <ModelInferForm
                model_id={props.model.id}
                model_version={props.modelversion.version_number}
                className="w-100"
                default_infer_data={'{"x":"y"}'}
              />
            )}
          </CModalBody>
        </CModal>
      )}

      <Button
        variant="contained"
        className="button-predict-now"
        disabled={props.disabled}
        color="primary"
        id="predict-now-button"
        data-attr="models:btn:open-predict-modal"
        onClick={() => {
          toggleShow(!show);
        }}
        sx={{ ...props.sx }}
        startIcon={<BoltOutlined />}
      >
        Predict Now
      </Button>
    </>
  );
}

ModelInferModalButton.propTypes = {
  sx: PropTypes.any,
  model: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  modelversion: PropTypes.number.isRequired,
};

//Button that displays a tooltip if the button title is too long
export const AtomAssistPersonaLinkTooltipButton = ({ link, onClick }) => {
  const [isTextTruncated, setIsTextTruncated] = useState(false);
  const textRef = useRef(null);

  const debouncedCheckTruncation = useCallback(
    debounce(() => {
      if (textRef.current) {
        const { offsetWidth, scrollWidth } = textRef.current;
        setIsTextTruncated(scrollWidth > offsetWidth);
      }
    }, 250), // 250ms delay
    [],
  );

  useEffect(() => {
    debouncedCheckTruncation();
    window.addEventListener("resize", debouncedCheckTruncation);
    return () => window.removeEventListener("resize", debouncedCheckTruncation);
  }, [link, debouncedCheckTruncation]);

  const button = (
    <Button
      color="primary"
      variant="contained"
      component="a"
      href={`/assistant/configs?delete-model-link=${link}`}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => onClick(e, link)}
      data-attr={`models:link:view-persona-${link}`}
      sx={{
        width: "200px",
        height: "36px",
        whiteSpace: "nowrap",
        display: "flex",
        overflow: "hidden",
        justifyContent: "center",
        alignItems: "center",
        "&:hover": {
          color: "#ffffff",
        },
      }}
      endIcon={<OpenInNewIcon />}
    >
      <span
        ref={textRef}
        style={{
          display: "block",
          overflow: "hidden",
          whiteSpace: "nowrap",
          width: "100%",
          textAlign: "center",
        }}
      >
        {link}
      </span>
    </Button>
  );

  return isTextTruncated ? (
    <Tooltip title={link} placement="top">
      {button}
    </Tooltip>
  ) : (
    button
  );
};

AtomAssistPersonaLinkTooltipButton.propTypes = {
  link: PropTypes.object,
  onClick: PropTypes.func,
};

export function ModelSwapPersonasControl({ modelId }) {
  const [targetModelId, setTargetModelId] = useState(null);
  const { mutate: swapPersonas, isLoading } = useSwapPersonasToModel();

  const handleSwap = () => {
    swapPersonas(
      { fromModelId: modelId, toModelId: targetModelId },
      {
        onSuccess: (data) => {
          const skipped = data?.skipped_seeded?.length
            ? ` ${data.skipped_seeded.length} seeded agent(s) were skipped: ${data.skipped_seeded.join(", ")}.`
            : "";
          NucToast({
            message: `Moved ${data?.updated_count ?? 0} agent(s) to the selected model.${skipped}`,
            type: "success",
          });
          setTargetModelId(null);
        },
        onError: (error) => {
          NucToast({
            message:
              error?.response?.data?.detail ||
              "Failed to move agents to the selected model",
            type: "error",
          });
        },
      },
    );
  };

  return (
    <div style={{ margin: "10px 0" }}>
      <ModelComboSelect
        label="Move all linked agents to another model"
        helperText="Re-points every linked agent to the selected model's latest version, which unblocks deletion"
        value={targetModelId}
        onlyDeployed
        onlyAgentBindable
        productId={AtomAssistProductID}
        onChange={(selected) => {
          if (selected?.value) {
            setTargetModelId(selected.value.id);
          }
        }}
      />
      {targetModelId === modelId ? (
        <ErrorMessageAlerts
          errorMessages={["Pick a different model than this one"]}
          colorType="warning"
        />
      ) : null}
      <CButton
        color="primary"
        onClick={handleSwap}
        disabled={!targetModelId || targetModelId === modelId || isLoading}
        style={{ marginTop: "5px" }}
        data-attr="models:btn:swap-personas"
      >
        {isLoading ? "Moving agents..." : "Move agents to selected model"}
      </CButton>
    </div>
  );
}

ModelSwapPersonasControl.propTypes = {
  modelId: PropTypes.number.isRequired,
};

export function ModelDeleteButton({ modelId, modelName, modelType }) {
  const history = useHistory();
  const [errorMessage, setErrorMessage] = useState(null);

  const { data: matchedPersona, isFetching } = useMatchingPersonas(
    modelId,
    modelType,
  );
  const {
    mutate: deleteModel,
    isSuccess,
    isError,
    error,
  } = useDeleteModel({ modelId });

  const handleClick = () => {
    deleteModel({ modelId });
  };

  useEffect(() => {
    if (matchedPersona && matchedPersona.length > 0) {
      setErrorMessage(
        "One or more agents are using this model, so it can't be deleted. " +
          "Move them all to another model below, or edit each agent individually.",
      );
    } else {
      setErrorMessage("");
    }
  }, [matchedPersona]);

  useEffect(() => {
    if (isError) {
      alert(error.response?.data?.detail);
    }
  }, [isError]);

  useEffect(() => {
    if (isSuccess) {
      history.push("/models");
    }
  }, [isSuccess]);

  const modal = React.createRef();
  const confirmMessage = `You are going to delete "${modelName}" model. Are you sure?`;
  const titleMessage = `Delete Model`;

  return (
    <>
      {isFetching ? (
        <>
          <ErrorMessageAlerts
            errorMessages={[
              "Checking if any agents are linked to this model...",
            ]}
            colorType="info"
          />
          <Loading height={"100px"} width={"100px"} />
        </>
      ) : null}
      {errorMessage !== "" && errorMessage && !isFetching ? (
        <>
          <ErrorMessageAlerts
            errorMessages={[errorMessage]}
            colorType="danger"
          />
          <ModelRelatedPersonas personas={matchedPersona} />
          <ModelSwapPersonasControl modelId={modelId} />
          <CButton
            color="danger"
            onClick={() => modal.current.showModal()}
            disabled
            data-attr="models:btn:delete-model"
          >
            Delete Model
          </CButton>
        </>
      ) : modelType !== "AtomAssist" ? (
        <>
          <CButton
            color="danger"
            onClick={() => modal.current.showModal()}
            style={{ marginTop: "5px" }}
            data-attr="models:btn:delete-model"
          >
            Delete Model
          </CButton>
          <ConfirmActionModal
            ref={modal}
            confirmMessage={confirmMessage}
            titleMessage={titleMessage}
            confirmAction={handleClick}
          />
        </>
      ) : !isFetching ? (
        <>
          <ErrorMessageAlerts
            errorMessages={[
              "No agents are linked to this model! Deletion of model is allowed",
            ]}
            colorType="success"
          />
          <CButton
            color="danger"
            onClick={() => modal.current.showModal()}
            style={{ marginTop: "5px" }}
            data-attr="models:btn:delete-model"
          >
            Delete Model
          </CButton>
          <ConfirmActionModal
            ref={modal}
            confirmMessage={confirmMessage}
            titleMessage={titleMessage}
            confirmAction={handleClick}
          />
        </>
      ) : isFetching ? (
        <>
          <CButton
            color="danger"
            onClick={() => modal.current.showModal()}
            disabled
            style={{ marginTop: "5px" }}
            data-attr="models:btn:delete-model"
          >
            Delete Model
          </CButton>
          <ConfirmActionModal
            ref={modal}
            confirmMessage={confirmMessage}
            titleMessage={titleMessage}
            confirmAction={handleClick}
          />
        </>
      ) : null}
    </>
  );
}

ModelDeleteButton.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  modelName: PropTypes.string,
  modelType: PropTypes.string,
};

export const SyncRemoteModelButton = (props) => {
  return (
    <div id="sync-remote-model-button">
      <CreateItemButton
        itemName="Sync Remote Model"
        createItemModal={SyncRemoteModelModal}
        createItemModalProps={{
          modelId: props.modelId,
        }}
        icon={<CloudDownloadOutlined />}
        fullWidth
      />
    </div>
  );
};

SyncRemoteModelButton.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export const UpdateRemoteUrlButton = (props) => {
  return (
    <div id="update-remote-url-button">
      <CreateItemButton
        itemName="Update Remote URL"
        createItemModal={UpdateRemoteUrlModal}
        createItemModalProps={{
          modelId: props.modelId,
          onSuccess: props.onSuccess,
        }}
        icon={<OpenInNewIcon />}
        fullWidth
      />
    </div>
  );
};

UpdateRemoteUrlButton.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onSuccess: PropTypes.func,
};

export const ModelAddNewVersionButton = (props) => {
  return (
    <CreateItemButton
      itemName="Model Version"
      disabled={props.readyModel}
      createItemModal={CreateModelVersionModal}
      createItemModalProps={{
        modelId: props.modelId,
      }}
      style={props.style}
    />
  );
};

ModelAddNewVersionButton.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  readyModel: PropTypes.bool,
  style: PropTypes.any,
};

export const downloadBulkPredictor = () => {
  window.location.href = "/files/Nuclearn_BulkPredictor.xlsm";
};

// eslint-disable-next-line no-unused-vars
function QuickStartSpreadsheetButton() {
  return (
    <>
      <CButton
        color="secondary"
        onClick={downloadBulkPredictor}
        className="button-bulk-predictor"
        block
      >
        Bulk Predictor{" "}
        <img
          src={`/images/favicon/excel_icon.png`}
          style={{ height: "20px" }}
        />
      </CButton>
    </>
  );
}

export const ScoreModelParamsLimit = (props) => {
  const [limitResults, setLimitResults] = useState(props.limitResults);
  useEffect(() => {
    setLimitResults(props.limitResults);
  }, [props.limitResults]);

  const [resultsLimitedTo, setResultsLimitedTo] = useState(
    props.resultsLimitedTo,
  );
  useEffect(() => {
    setResultsLimitedTo(props.resultsLimitedTo);
  }, [props.resultsLimitedTo]);

  const handleLimitResultsChange = (e) => {
    const limitResults = e.target.value;

    let resultsLimitedTo = null;
    // when the value changes from no to topn, set the default value to 10
    if (limitResults === "topn") {
      resultsLimitedTo = props.defaultTopNResultsLimitedTo;
    } else if (limitResults === "aboveprob") {
      resultsLimitedTo = props.defaultAboveProbResultsLimitedTo;
    }
    setLimitResults(limitResults);
    setResultsLimitedTo(resultsLimitedTo);
    props.onLimitResultsChange(limitResults);
    props.onResultsLimitedToChange(resultsLimitedTo);
  };

  const handleResultsLimitedToChange = (e) => {
    setResultsLimitedTo(e.target.value);
    props.onResultsLimitedToChange(e.target.value);
  };

  return (
    <>
      <CFormGroup variant="custom-radio" inline>
        <CInputRadio
          className="form-check-input"
          id="limitResults1"
          name="limitresults"
          value="no"
          onChange={handleLimitResultsChange}
          disabled={props.disabled}
          checked={limitResults === "no"}
        />
        <CLabel variant="checkbox" htmlFor="limitResults1">
          No
        </CLabel>
      </CFormGroup>
      <CFormGroup variant="custom-radio" inline>
        <CInputRadio
          className="form-check-input"
          id="limitResults2"
          name="limitresults"
          value="topn"
          onChange={handleLimitResultsChange}
          disabled={props.disabled}
          checked={limitResults === "topn"}
        />
        <CLabel variant="checkbox" htmlFor="limitResults2">
          Top N Predictions
        </CLabel>
      </CFormGroup>
      <CFormGroup variant="custom-radio" inline>
        <CInputRadio
          className="form-check-input"
          id="limitResults3"
          name="limitresults"
          value="aboveprob"
          onChange={handleLimitResultsChange}
          disabled={props.disabled}
          checked={limitResults === "aboveprob"}
        />
        <CLabel variant="checkbox" htmlFor="limitResults3">
          Predictions Above Probability
        </CLabel>
      </CFormGroup>
      <br />
      <br />
      {/*TODO: form validation*/}
      {limitResults === "topn" && (
        <CFormGroup>
          <CInput
            id="resultsLimitedToN"
            value={resultsLimitedTo}
            required
            onChange={handleResultsLimitedToChange}
            disabled={props.disabled}
          />
        </CFormGroup>
      )}
      {limitResults === "aboveprob" && (
        <CFormGroup>
          <CInput
            id="resultsLimitedToPct"
            value={resultsLimitedTo}
            required
            onChange={handleResultsLimitedToChange}
            style={{
              display: "inline",
              width: "auto",
              textAlign: "right",
            }}
            disabled={props.disabled}
          />
          %
        </CFormGroup>
      )}
    </>
  );
};

ScoreModelParamsLimit.propTypes = {
  onLimitResultsChange: PropTypes.func.isRequired,
  onResultsLimitedToChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  limitResults: PropTypes.string,
  resultsLimitedTo: PropTypes.number,
  defaultTopNResultsLimitedTo: PropTypes.number,
  defaultAboveProbResultsLimitedTo: PropTypes.number,
};

ScoreModelParamsLimit.defaultProps = {
  limitResults: "aboveprob",
  resultsLimitedTo: 75.0,
  defaultTopNResultsLimitedTo: 10,
  defaultAboveProbResultsLimitedTo: 75,
  disabled: false,
};

export function UploadSpreadsheetButton(props) {
  const [modalShow, setModalShow] = useState(false);
  const [exitModalConfirm, setExitModalConfirm] = useState(false);
  const [limitResults, setLimitResults] = useState("topn");
  const [columnsOrRows, setColumnsOrRows] = useState("columns");
  const [resultsLimitedTo, setResultsLimitedTo] = useState(5);
  const [includeInputData, setIncludeInputData] = useState(false);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [waitingToDownload, setWaitingToDownload] = useState(false);
  const [percentComplete, setPercentComplete] = useState(0.0);
  const [errorMessage, setErrorMessage] = useState(null);

  const inputFile = useRef(null); // reference to the file that gets uploaded

  const resetToDefault = () => {
    setModalShow(false);
    setExitModalConfirm(false);
    setLimitResults("topn");
    setColumnsOrRows("columns");
    setResultsLimitedTo(5);
    setIncludeInputData(false);
    setFileToUpload(null);
    setWaitingToDownload(false);
    setPercentComplete(0.0);
    setErrorMessage(null);
    if (inputFile.current) inputFile.current.value = "";
  };

  const handleModalClose = () => {
    // Prompt a confirmation if user is trying to leave modal while waiting for download
    if (waitingToDownload) {
      setExitModalConfirm(true);
    } else {
      resetToDefault();
    }
  };

  const handleError = (err) => {
    // if there is an error, reset the modal and show the error message
    setPercentComplete(0.0);
    setWaitingToDownload(false);

    var errval = "";
    try {
      // Show the detail if returned.  If nothing returned, then this is a weird server error
      // eslint-disable-next-line no-redeclare
      var errval = err.response.data.detail;
      if (typeof errval !== "string") {
        errval = "Unknown error";
      }
      // eslint-disable-next-line no-unused-vars
    } catch (error) {
      errval = "Unknown error";
    }

    setErrorMessage(errval);
  };
  const downloadTheFile = (data) => {
    // run and download the file
    const BOM = new Uint8Array([0xef, 0xbb, 0xbf]); //https://stackoverflow.com/questions/6672834/specifying-blob-encoding-in-google-chrome
    let blob = new Blob([BOM, data.data], { type: "text/plain;charset=utf8" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = "Results.csv";
    link.click();
  };
  const updateStatus = (data) => {
    // Loop over refresh status, updating the progress bar as we go
    let { job_id } = data;
    ModelService.get_bulkscore_spreadsheet_status(
      props.modelId,
      props.modelVersionId,
      job_id,
      (_data) => {
        // This is the on sucess callback, basically update the progress bar and request status again after delay
        setPercentComplete(_data.pct_complete);
        if (_data.pct_complete === 1.0) {
          // Job complete, download file
          setWaitingToDownload(false);
          ModelService.bulkscore_spreadsheet_result(
            props.modelId,
            props.modelVersionId,
            job_id,
            downloadTheFile,
            handleError,
          );
        } else {
          //update PCT Complete field, re-ping in 2.5 seconds
          setTimeout(() => {
            updateStatus(_data);
          }, 2500);
        }
      },
      handleError,
    );
  };

  const uploadFile = () => {
    inputFile.current.value = ""; // have to clear, otherwise "OnChange" event of input may not be called
    inputFile.current.click();
  };

  const newFile = (event) => {
    // Stop a form submit, intercept the file, and ask user to provide input parameters
    event.stopPropagation();
    event.preventDefault();
    setFileToUpload(event.target.files[0]);
    setModalShow(true);
    return false;
  };

  const uploadAndScore = () => {
    // Pass parameters and file to the server, and check on status
    setWaitingToDownload(true);
    let rlt = resultsLimitedTo;
    if (limitResults === "aboveprob") {
      rlt /= 100.0;
    }
    ModelService.submit_bulkscore_spreadsheet(
      props.modelId,
      props.modelVersionId,
      limitResults,
      rlt,
      columnsOrRows,
      includeInputData,
      fileToUpload,
      updateStatus,
      handleError,
    );
  };

  return (
    <>
      <input
        type="file"
        id="uploadspreadsheetinput"
        ref={inputFile}
        style={{ display: "none" }}
        onChange={newFile}
      />
      <CButton
        color="secondary"
        onClick={uploadFile}
        className="button-upload-spreadsheet"
        block
        hidden
        style={{ ...props.style }}
      >
        Score Spreadsheet{" "}
        <img
          src={`/images/favicon/excel_icon.png`}
          style={{ height: "20px" }}
        />
      </CButton>
      <CModal show={modalShow} closeOnBackdrop={false}>
        <CModalHeader>
          <CModalTitle>Score Spreadsheet</CModalTitle>
          <button
            className="close"
            aria-label="Close"
            onClick={() => handleModalClose()}
          >
            ×
          </button>
        </CModalHeader>
        <CModalBody>
          <CModal
            show={exitModalConfirm}
            size="sm"
            onClose={() => setExitModalConfirm(false)}
            style={{ marginTop: "50px" }}
          >
            <CModalBody>
              <b>
                Are you sure you want to exit? You will not be able to download
                your results if you leave this screen.
              </b>
              <br />
              <br />
              <div style={{ textAlign: "center" }}>
                <CButton
                  color="primary"
                  data-attr="models:btn:keep-results"
                  onClick={() => {
                    setExitModalConfirm(false);
                  }}
                >
                  No, I want my results
                </CButton>
                <br />
                <br />
                <CButton
                  color="danger"
                  data-attr="models:btn:exit-score-modal"
                  onClick={() => {
                    resetToDefault();
                  }}
                >
                  Yes, I want to exit
                </CButton>
              </div>
            </CModalBody>
          </CModal>
          {errorMessage && <CAlert color="danger">{errorMessage}</CAlert>}
          {!errorMessage && !waitingToDownload && (
            <>
              {/*Add filter for % filter*/}
              <CFormGroup row>
                <CCol md="4">
                  <CLabel>Limit Predictions</CLabel>
                </CCol>
                <CCol md="8">
                  <ScoreModelParamsLimit
                    onLimitResultsChange={(val) => {
                      setLimitResults(val);
                    }}
                    onResultsLimitedToChange={(val) => {
                      setResultsLimitedTo(val);
                    }}
                  />
                </CCol>
              </CFormGroup>
              {/*radio selection for prediction*/}
              <CFormGroup row>
                <CCol md="4">
                  <CLabel>Predictions in Columns or Rows?</CLabel>
                </CCol>
                <CCol md="8">
                  <CFormGroup variant="custom-radio" inline>
                    <CInputRadio
                      defaultChecked
                      className="form-check-input"
                      id="colsOrRows1"
                      name="columnsorrows"
                      value="columns"
                      onChange={(event) => {
                        setColumnsOrRows(event.target.value);
                      }}
                    />
                    <CLabel variant="checkbox" htmlFor="colsOrRows1">
                      Columns
                    </CLabel>
                  </CFormGroup>
                  <CFormGroup variant="custom-radio" inline>
                    <CInputRadio
                      className="form-check-input"
                      id="colsOrRows2"
                      name="columnsorrows"
                      value="rows"
                      onChange={(event) => {
                        setColumnsOrRows(event.target.value);
                      }}
                    />
                    <CLabel variant="checkbox" htmlFor="colsOrRows2">
                      Rows
                    </CLabel>
                  </CFormGroup>
                </CCol>
              </CFormGroup>
              <CFormGroup row>
                <CCol md="4">
                  <CLabel>Output Format</CLabel>
                </CCol>
                <CCol md="8">
                  <CInputCheckbox
                    className="form-check-input"
                    id="includeInputData"
                    name="checkbox"
                    value="includeInputData"
                    defaultChecked={includeInputData}
                    onChange={() => {
                      setIncludeInputData(!includeInputData);
                    }}
                  />
                  <CLabel variant="checkbox" htmlFor="includeInputData">
                    Include input data in output?
                  </CLabel>
                </CCol>
              </CFormGroup>
            </>
          )}
          {waitingToDownload && (
            <>
              <h4>File processing...</h4>
              <br />
              <h6>
                <progress max="1" value={percentComplete}></progress>{" "}
                {Math.round(100 * percentComplete)}% Complete
              </h6>
              <br />
              <p>
                <b>Do not exit this screen until download is complete!</b>
                <br />
                <i>
                  When file is finished processing, it will automatically
                  download.
                </i>
              </p>
            </>
          )}
        </CModalBody>
        {!errorMessage && !waitingToDownload && (
          <CModalFooter>
            <CButton
              color="primary"
              onClick={uploadAndScore}
              data-attr="models:btn:score-spreadsheet"
            >
              Score Spreadsheet
            </CButton>
          </CModalFooter>
        )}
      </CModal>
    </>
  );
}

UploadSpreadsheetButton.propTypes = {
  modelId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  modelVersionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
    .isRequired,
  style: PropTypes.any,
};

export const downloadIssueTrendingDashboard = () => {
  window.location.href = `/files/Nuclearn_CRTrending.xlsm`;
};

// eslint-disable-next-line no-unused-vars
function IssueTrendingDashboardButton() {
  return (
    <>
      <CButton
        color="dark"
        onClick={downloadIssueTrendingDashboard}
        className="button-issue-trending-dashboard"
      >
        <div style={{ display: "flex", alignItems: "center" }}>
          <span>Condition Report Trending Dashboard</span>

          <img
            src={`/images/favicon/excel_icon.png`}
            style={{ height: "20px", margin: "0px 5px 0px 5px" }}
          />
        </div>
      </CButton>
    </>
  );
}

export function BlogPostButton(props) {
  return (
    <>
      <a target="_blank" href={props.url} rel="noreferrer">
        {props.title}{" "}
        <CIcon content={freeSet.cilRss} style={{ marginTop: "-1px" }} />
      </a>
    </>
  );
}

BlogPostButton.propTypes = {
  title: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
};

export const AssociateDatasetButton = (props) => {
  const [show, toggleShow] = useState(false);

  const actionComplete = () => toggleShow(false);

  return (
    <>
      {show && (
        <CModal
          show={show}
          onClose={() => {
            toggleShow(!show);
          }}
          size="xl"
          className="text-center modal-center"
          data-testid="mdl-map-dataset-dialog"
        >
          <CModalHeader closeButton>
            <CModalTitle>{props.modaltitle}</CModalTitle>
          </CModalHeader>
          <CModalBody>
            {React.cloneElement(props.children, { closeModal: actionComplete })}
          </CModalBody>
        </CModal>
      )}

      <Button
        disabled={props.disabled}
        onClick={() => {
          toggleShow(!show);
        }}
        color="primary"
        variant="contained"
        {...props}
        sx={{ ...props.sx }}
        startIcon={!props.hideIcon && <MapOutlined />}
        data-attr="models:btn:associate-dataset"
      >
        {props.executecontents}
      </Button>
    </>
  );
};

AssociateDatasetButton.propTypes = {
  modaltitle: PropTypes.string.isRequired,
  executecontents: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
    PropTypes.string,
  ]).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  color: PropTypes.string,
  disabled: PropTypes.bool,
  sx: PropTypes.any,
  hideIcon: PropTypes.bool,
};

AssociateDatasetButton.defaultProps = {
  hideIcon: false,
};
